<template>
  <div>
    <div>
      <FilterCard :applied-filter-count="appliedFilterCount">
        <b-row>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Dispatched Date"
              label-for="dispatched-date"
            >
              <div class="position-relative">
                <AdvancedDateFilter
                  :date-value="filters['created_at']"
                  :opens="'right'"
                  @update="updateDateRange('created_at', $event)"
                />
              </div>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Warehouse"
              label-for="warehouse"
            >
              <v-select
                v-model="filters['branch_id']"
                :options="warehouseOptions"
                :reduce="option => option.id"
                label="name"
                placeholder="Select Warehouse"
              >
                <template v-slot:option="option">
                  {{ option.name }}
                </template>
              </v-select>
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Apply and Clear Filters -->
        <b-row class="mt-1">
          <b-col>
            <filter-buttons
              :filter-data="filters"
              :apply-filters="applyFilters"
              :clear-filters="clearFilters"
            />
          </b-col>
        </b-row>
      </FilterCard>
    </div>
    <div>
      <b-card no-body>
        <div class="m-2">
          <b-row>
            <b-col
              class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
            >
              <div>
                <ReportTableButtons
                  :items="items"
                  :json_fieldz="json_fields"
                  :name="`${downloadReportName} - Page ${page}.xls`"
                  :bulkname="`${downloadReportName}.xls`"
                  :fetch="fetchBranchWiseDispatchedReportNoPagination"
                  @refresh="fetchBranchWiseDispatchedReport"
                />
              </div>
            </b-col>
          </b-row>
        </div>
        <b-overlay
          id="overlay-background"
          blur="10px"
          opacity="0.30"
          rounded="sm"
          variant="light"
        >
          <b-table
            striped
            hover
            responsive
            :per-page="perPage"
            :items="items"
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            show-empty
            sticky-header="100vh"
            @sort-changed="sortChanged"
          >
            <template #empty>
              <TableDataFetching
                :rows="items"
                :data-loading="dataLoading"
              />
            </template>
            <template #head()="{ field }">
              <div class="sortable-header">
                <span class="header-text">{{ field.label }}</span>
                <feather-icon
                  v-if="field.sortable"
                  :icon="sort === field.key
                    ? 'ArrowUpIcon'
                    : 'ArrowDownIcon'"
                  size="14"
                />
              </div>
            </template>
            <!-- Custom Footer Summary: Daily Total Across All Branches -->
            <template #custom-foot>
              <b-tr
                v-if="items.length > 0"
                class="font-weight-bolder bg-light"
              >
                <b-td class="text-left">
                  Total
                </b-td>
                <b-td
                  v-for="dateKey in dynamicDateKeys"
                  :key="dateKey"
                  class="text-right"
                >
                  {{ __numberWithCommas(calculateDailyTotal(dateKey)) }}
                </b-td>
                <b-td class="text-right">
                  {{ __numberWithCommas(calculateGrandTotal()) }}
                </b-td>
              </b-tr>
            </template>
          </b-table>
        </b-overlay>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              md="3"
              class="d-flex align-items-center justify-content-sm-start"
            >
              <label class="mr-1">Per page</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="pageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block width-125"
              />
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              md="9"
              class="d-flex align-items-center justify-content-sm-end"
            >
              <span class="text-muted pagination-text mt-1 mt-md-0 mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
              <b-pagination
                v-model="page"
                :total-rows="(meta.total) ? meta.total : 0"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BFormGroup,
  BRow,
  BTable,
  BTr,
  BTd,
  VBModal,
  BOverlay,
  BPagination,
} from 'bootstrap-vue'
import Filter from '@core/mixins/filter'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import vSelect from 'vue-select'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import FilterButtons from '@/components/Filter/ReportFilterButtons.vue'
import tableMixin from '@core/mixins/tableMixin'
import FilterCard from '@/components/FilterCard/FilterCard.vue'

const ReportRepository = RepositoryFactory.get('report')
const ResourceRepository = RepositoryFactory.get('resource')

export default {
  directives: {
    'b-modal': VBModal,
  },
  components: {
    vSelect,
    AdvancedDateFilter,
    BCard,
    BTable,
    BRow,
    BCol,
    BTr,
    BTd,
    BFormGroup,
    BOverlay,
    BPagination,
    FilterButtons,
    FilterCard,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage, tableMixin],
  data() {
    return {
      filters: {
        created_at: {
          startDate: null,
          endDate: null,
        },
        branch_id: null,
      },
      tableData: [],
      dataLoading: false,
      sort: '',
      perPage: 20,
      pageOptions: [10, 20, 50, 100, 200],
      totalRows: 1,
      page: 1,
      meta: {},
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      filter: null,
      filterOn: [],
      fields: [],
      dynamicDateKeys: [],
      json_fields: {},
      downloadReportName: 'Branch-Wise-Dispatched-Report',
      items: [],
      warehouseOptions: [],
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchBranchWiseDispatchedReport()
      },
    },
    deep: true,
    page() {
      this.fetchBranchWiseDispatchedReport()
    },
    perPage() {
      this.fetchBranchWiseDispatchedReport()
    },
  },
  mounted() {
    this.totalRows = this.items.length
    this.setLast15DaysFilter()
    this.fetchBranchWiseDispatchedReport()
    this.getWarehouseList()
    this.updateFilterCount()
  },
  methods: {
    async getWarehouseList() {
      try {
        this.warehouseOptions = localStorage.getItem('warehouse_list')
          ? JSON.parse(localStorage.getItem('warehouse_list'))
          : (await ResourceRepository.getWarehouseListForDropDown()).data.data
        if (!localStorage.getItem('warehouse_list')) localStorage.setItem('warehouse_list', JSON.stringify(this.warehouseOptions))
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async fetchBranchWiseDispatchedReport() {
      this.dataLoading = true
      this.items = []
      const filterQuery = this.buildFilterQuery()
      try {
        const { data } = await ReportRepository.getBranchWiseDispatchedReport(this.page, filterQuery, this.perPage)
        this.items = data.data
        this.meta = data.meta

        // Dynamically build headers based on the dates returned
        this.buildDynamicFields()
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async fetchBranchWiseDispatchedReportNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const { data } = await ReportRepository.getBranchWiseDispatchedReportNoPagination(filterQuery)
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },

    buildDynamicFields() {
      const fixedLeft = [
        {
          key: 'branch_name',
          label: 'Branch Name',
          thClass: 'text-left',
          tdClass: 'text-left',
          sortable: true,
        },
      ]

      this.dynamicDateKeys = []
      const dateColumns = []
      const jsonFields = {
        'Branch Name': {
          field: 'branch_name',
          callback: value => `"${value}"`,
        },
      }

      if (this.items && this.items.length > 0) {
        const sampleRow = this.items[0]
        const ignoredKeys = ['branch_id', 'branch_name', 'warehouse_id', 'total_dispatched']

        Object.keys(sampleRow).forEach(key => {
          if (!ignoredKeys.includes(key)) {
            this.dynamicDateKeys.push(key)
            dateColumns.push({
              key,
              label: key, // e.g. "07 Oct"
              thClass: 'text-right',
              tdClass: 'text-right',
              sortable: false,
              // eslint-disable-next-line no-underscore-dangle
              formatter: value => this.__numberWithCommas(value || 0), // Built-in formatting
            })
            jsonFields[key] = key
          }
        })
      }

      const fixedRight = [
        {
          key: 'total_dispatched',
          label: 'Total Dispatched',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
          // eslint-disable-next-line no-underscore-dangle
          formatter: value => this.__numberWithCommas(value || 0), // Built-in formatting
        },
      ]
      jsonFields['Total Dispatched'] = 'total_dispatched'

      this.fields = [...fixedLeft, ...dateColumns, ...fixedRight]
      this.json_fields = jsonFields
    },

    // Footer calculations: Daily total for a specific date column
    calculateDailyTotal(dateKey) {
      return this.items.reduce((sum, item) => sum + (Number(item[dateKey]) || 0), 0)
    },

    // Footer calculation: Grand total across all branches
    calculateGrandTotal() {
      return this.items.reduce((sum, item) => sum + (Number(item.total_dispatched) || 0), 0)
    },

    setLast15DaysFilter() {
      const last15Days = this.last15DaysFilter()
      this.updateDateRange('created_at', last15Days)
    },
    applyFilters() {
      this.fetchBranchWiseDispatchedReport()
      this.updateFilterCount()
    },
    clearFilters() {
      this.filters = {
        created_at: {
          startDate: null,
          endDate: null,
        },
        branch_id: null,
      }
      this.setLast15DaysFilter()
      this.$nextTick(() => {
        this.fetchBranchWiseDispatchedReport()
      })
      this.updateFilterCount()
    },
  },
}
</script>

<style lang="scss">
@import '../../../@core/scss/vue/libs/vue-select';
</style>
